const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

class ApiClient {
  constructor() {
    this.token = localStorage.getItem('auth_token');
  }

  setToken(token) {
    this.token = token;
    localStorage.setItem('auth_token', token);
  }

  clearToken() {
    this.token = null;
    localStorage.removeItem('auth_token');
  }

  async request(endpoint, options = {}) {
    const url = `${API_URL}${endpoint}`;
    const headers = {
      'Content-Type': 'application/json',
      ...options.headers,
    };

    if (this.token) {
      headers['Authorization'] = `Bearer ${this.token}`;
    }

    const response = await fetch(url, {
      ...options,
      headers,
    });

    if (!response.ok) {
      if (response.status === 401) {
        this.clearToken();
        window.location.href = '/login';
      }
      const error = await response.json();
      throw new Error(error.error || 'API Error');
    }

    if (response.status === 204) return null;
    return response.json();
  }

  // Upload multipart (FormData) — non impostare Content-Type, lo fa il browser
  async upload(endpoint, formData) {
    const url = `${API_URL}${endpoint}`;
    const headers = {};
    if (this.token) {
      headers['Authorization'] = `Bearer ${this.token}`;
    }

    const response = await fetch(url, { method: 'POST', headers, body: formData });

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.error || 'Upload Error');
    }
    return response.json();
  }

  // Auth
  auth = {
    login: (email, password) => this.request('/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    }),
    verify: () => this.request('/auth/verify'),
  };

  // Categories
  categories = {
    list: () => this.request('/categories'),
    get: (id) => this.request(`/categories/${id}`),
    create: (data) => this.request('/categories', { method: 'POST', body: JSON.stringify(data) }),
    update: (id, data) => this.request(`/categories/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
    delete: (id) => this.request(`/categories/${id}`, { method: 'DELETE' }),
  };

  // Illustrations
  illustrations = {
    list: (status) => this.request(`/illustrations${status ? `?status=${status}` : ''}`),
    get: (id) => this.request(`/illustrations/${id}`),
    create: (data) => this.request('/illustrations', { method: 'POST', body: JSON.stringify(data) }),
    update: (id, data) => this.request(`/illustrations/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
    delete: (id) => this.request(`/illustrations/${id}`, { method: 'DELETE' }),
  };

  // Distributors
  distributors = {
    list: () => this.request('/distributors'),
    get: (id) => this.request(`/distributors/${id}`),
    create: (data) => this.request('/distributors', { method: 'POST', body: JSON.stringify(data) }),
    update: (id, data) => this.request(`/distributors/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
    delete: (id) => this.request(`/distributors/${id}`, { method: 'DELETE' }),
  };

  // Consignments
  consignments = {
    list: () => this.request('/consignments'),
    listByDistributor: (distributor_id) => this.request(`/consignments/distributor/${distributor_id}`),
    create: (data) => this.request('/consignments', { method: 'POST', body: JSON.stringify(data) }),
    update: (id, data) => this.request(`/consignments/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
    delete: (id) => this.request(`/consignments/${id}`, { method: 'DELETE' }),
  };

  // Settings
  settings = {
    list: () => this.request('/settings'),
    bulkUpdate: (updates) => this.request('/settings', {
      method: 'PUT',
      body: JSON.stringify({ updates }),
    }),
  };

  // Formats
  formats = {
    list: () => this.request('/formats'),
    create: (data) => this.request('/formats', { method: 'POST', body: JSON.stringify(data) }),
    update: (id, data) => this.request(`/formats/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
    delete: (id) => this.request(`/formats/${id}`, { method: 'DELETE' }),
  };

  // Videos
  videos = {
    list: (visible) => this.request(`/videos${visible !== undefined ? `?visible=${visible}` : ''}`),
    create: (data) => this.request('/videos', { method: 'POST', body: JSON.stringify(data) }),
    update: (id, data) => this.request(`/videos/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
    delete: (id) => this.request(`/videos/${id}`, { method: 'DELETE' }),
  };

  // Images (self-hosted, sostituisce Cloudinary)
  images = {
    list: () => this.request('/images'),
    upload: (file) => {
      const formData = new FormData();
      formData.append('file', file);
      return this.upload('/images', formData);
    },
    delete: (public_id) => this.request('/images', {
      method: 'DELETE',
      body: JSON.stringify({ public_id }),
    }),
  };

  // Email
  email = {
    sendCart: (payload) => this.request('/email/cart', {
      method: 'POST',
      body: JSON.stringify(payload),
    }),
  };
}

export const api = new ApiClient();
